/**
 * ProjectsPage - List and manage Flux COLD projects
 * Follows Site DNA pattern with table rows and expanded drawers
 */

import React, { useState, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    IconButton,
    Alert,
    CircularProgress,
    Collapse,
    TableCell,
    TableRow,
    Chip,
    alpha,
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    AcUnit as ColdIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    Folder as FolderIcon,
    Warehouse as WarehouseIcon,
    LocationOn as LocationIcon,
    FolderOpen as FolderOpenIcon,
} from '@mui/icons-material';
import {
    vortxColors,
    vortxTypography,
    useThemeMode,
    ProjectTableContainer,
    ProjectTableHead,
    ProjectTableHeaderCell,
    ProjectTableBody,
    AddExistingProjectDialog,
} from '@vortx/shared-ui';
import { useColdProjects } from '../hooks/useColdProjects';

export default function ProjectsPage() {
    const navigate = useNavigate();
    const { mode, isDark } = useThemeMode();
    const { projects, loading, error, createProject, deleteProject, updateProject, loadProjects } = useColdProjects();

    const [addExistingOpen, setAddExistingOpen] = useState(false);
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [newProjectName, setNewProjectName] = useState('');
    const [newProjectDescription, setNewProjectDescription] = useState('');
    const [creating, setCreating] = useState(false);

    // Expanded projects state
    const [expandedProjects, setExpandedProjects] = useState({});

    // Rename dialog
    const [renameDialogOpen, setRenameDialogOpen] = useState(false);
    const [projectToRename, setProjectToRename] = useState(null);
    const [tempProjectName, setTempProjectName] = useState('');

    // Delete dialog
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [projectToDelete, setProjectToDelete] = useState(null);

    const isRetro = mode === 'retro';

    // Theme-aware colors
    const bgCard = isRetro ? 'rgba(0, 0, 0, 0.4)' : alpha('#2196F3', 0.05);
    const textPrimary = isRetro ? '#f4f1ec' : 'text.primary';
    const textSecondary = isRetro ? '#888' : 'text.secondary';

    const toggleExpand = (projectId) => {
        setExpandedProjects(prev => ({
            ...prev,
            [projectId]: !prev[projectId]
        }));
    };

    const isExpanded = (projectId) => expandedProjects[projectId] || false;

    const handleCreateProject = async () => {
        if (!newProjectName.trim()) return;

        setCreating(true);
        try {
            const project = await createProject({
                name: newProjectName,
                description: newProjectDescription,
                location: {
                    address: '',
                    lat: null,
                    lon: null
                },
                apps: {
                    flux_cold: {
                        enabled: true,
                        config: {
                            zones: [],
                            ambient_temps: Array(24).fill(60),
                            energy_prices: Array(24).fill(0.12),
                            demand_charge: 15.0,
                            tariff_id: null
                        }
                    }
                }
            });
            setCreateDialogOpen(false);
            setNewProjectName('');
            setNewProjectDescription('');
            navigate(`/cold-storage/${project.id}`);
        } catch (err) {
            console.error('Failed to create project:', err);
        } finally {
            setCreating(false);
        }
    };

    const openRenameDialog = (project, e) => {
        e.stopPropagation();
        setProjectToRename(project);
        setTempProjectName(project.name);
        setRenameDialogOpen(true);
    };

    const handleRenameProject = async () => {
        if (!tempProjectName.trim() || !projectToRename) return;

        try {
            await updateProject(projectToRename.id, { name: tempProjectName });
            setRenameDialogOpen(false);
            setProjectToRename(null);
            setTempProjectName('');
        } catch (err) {
            console.error('Failed to rename project:', err);
        }
    };

    const openDeleteDialog = (project, e) => {
        e.stopPropagation();
        setProjectToDelete(project);
        setDeleteDialogOpen(true);
    };

    const handleDeleteProject = async () => {
        if (!projectToDelete) return;

        try {
            await deleteProject(projectToDelete.id);
            setDeleteDialogOpen(false);
            setProjectToDelete(null);
        } catch (err) {
            console.error('Failed to delete project:', err);
        }
    };

    const getFacilityCount = (project) => {
        return project.apps?.flux_cold?.config?.zones?.length || 0;
    };

    const getProjectLocation = (project) => {
        const loc = project.location;
        if (loc?.address) return loc.address;
        if (loc?.lat && loc?.lon) return `${loc.lat.toFixed(2)}, ${loc.lon.toFixed(2)}`;
        return 'No location set';
    };

    return (
        <Box sx={{ p: { xs: 2, md: 4 } }}>
            {/* Header */}
            <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                    {isRetro ? (
                        <>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                                <ColdIcon sx={{ fontSize: '2rem', color: '#2196F3' }} />
                                <Typography sx={{
                                    fontFamily: vortxTypography.heading,
                                    fontSize: '1.5rem',
                                    fontWeight: 700,
                                    color: '#2196F3',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.15em',
                                    textShadow: `0 0 20px rgba(33, 150, 243, 0.4)`,
                                }}>
                                    Cold Storage Projects
                                </Typography>
                            </Box>
                            <Typography sx={{
                                fontFamily: vortxTypography.mono,
                                fontSize: '0.75rem',
                                color: '#888',
                                pl: 3,
                                fontStyle: 'italic',
                            }}>
                                expand a project to view details
                            </Typography>
                        </>
                    ) : (
                        <>
                            <Typography variant="h3" sx={{
                                fontFamily: vortxTypography.heading,
                                color: '#2196F3',
                                fontWeight: 700,
                                mb: 1,
                                letterSpacing: '0.02em'
                            }}>
                                YOUR PROJECTS
                            </Typography>
                            <Typography variant="body1" sx={{
                                fontFamily: vortxTypography.body,
                                color: 'text.secondary',
                                fontSize: '1.1rem'
                            }}>
                                Manage your cold storage facility optimization projects
                            </Typography>
                        </>
                    )}
                </Box>
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <Button
                        variant="outlined"
                        startIcon={<FolderOpenIcon />}
                        onClick={() => setAddExistingOpen(true)}
                        sx={isRetro ? {
                            color: '#2196F3',
                            borderColor: 'rgba(33, 150, 243, 0.5)',
                            fontFamily: vortxTypography.mono,
                            fontWeight: 600,
                            fontSize: '0.8rem',
                            borderRadius: 0,
                            textTransform: 'uppercase',
                            letterSpacing: '0.1em',
                            '&:hover': {
                                borderColor: '#2196F3',
                                bgcolor: 'rgba(33, 150, 243, 0.1)',
                                boxShadow: '0 0 15px rgba(33, 150, 243, 0.3)',
                            }
                        } : {
                            color: '#2196F3',
                            borderColor: '#2196F3',
                            fontFamily: vortxTypography.body,
                            fontWeight: 600,
                            borderRadius: 2,
                            textTransform: 'none',
                            '&:hover': {
                                borderColor: '#1976d2',
                                bgcolor: alpha('#2196F3', 0.1),
                            }
                        }}
                    >
                        Add Existing
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setCreateDialogOpen(true)}
                        sx={isRetro ? {
                            bgcolor: 'transparent',
                            color: '#2196F3',
                            px: 3,
                            py: 1,
                            fontSize: '0.8rem',
                            fontWeight: 600,
                            fontFamily: vortxTypography.mono,
                            borderRadius: 0,
                            textTransform: 'uppercase',
                            letterSpacing: '0.1em',
                            border: '1px solid rgba(33, 150, 243, 0.5)',
                            boxShadow: 'none',
                            '&:hover': {
                                bgcolor: 'rgba(33, 150, 243, 0.1)',
                                borderColor: '#2196F3',
                                boxShadow: '0 0 15px rgba(33, 150, 243, 0.3)',
                            }
                        } : {
                            bgcolor: '#2196F3',
                            color: '#FFFFFF',
                            px: 4,
                            py: 1.5,
                            fontSize: '1rem',
                            fontWeight: 600,
                            fontFamily: vortxTypography.body,
                            borderRadius: 2,
                            textTransform: 'none',
                            boxShadow: `0 2px 8px ${alpha('#2196F3', 0.3)}`,
                            '&:hover': {
                                bgcolor: '#1976d2',
                                boxShadow: `0 4px 12px ${alpha('#2196F3', 0.4)}`,
                            }
                        }}
                    >
                        New Project
                    </Button>
                </Box>
            </Box>

            {/* Error Alert */}
            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {/* Projects Table */}
            {loading ? (
                <Box
                    className={`vx-stagger ${isRetro ? 'retro-mode' : ''}`}
                    sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
                >
                    {[1, 2, 3].map((i) => (
                        <Box
                            key={i}
                            className="vx-skeleton vx-slide-in-up"
                            sx={{
                                height: 60,
                                borderRadius: isRetro ? 0 : 2
                            }}
                        />
                    ))}
                </Box>
            ) : projects.length === 0 ? (
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    py: isRetro ? 8 : 12,
                    px: 3
                }}>
                    <Box sx={{
                        bgcolor: isRetro ? 'transparent' : alpha('#2196F3', 0.1),
                        border: isRetro ? '1px solid rgba(33, 150, 243, 0.3)' : 'none',
                        borderRadius: isRetro ? 0 : '50%',
                        p: 3,
                        mb: 3,
                    }}>
                        <ColdIcon sx={{ fontSize: isRetro ? 48 : 64, color: '#2196F3', opacity: isRetro ? 0.6 : 0.8 }} />
                    </Box>
                    <Typography sx={{
                        fontFamily: vortxTypography.heading,
                        fontSize: isRetro ? '1.2rem' : '1.5rem',
                        fontWeight: isRetro ? 700 : 600,
                        color: isRetro ? '#2196F3' : textPrimary,
                        textTransform: isRetro ? 'uppercase' : 'none',
                        letterSpacing: isRetro ? '0.1em' : 'normal',
                        mb: 1,
                    }}>
                        {isRetro ? 'Archive Empty' : 'No Projects Yet'}
                    </Typography>
                    <Typography sx={{
                        fontFamily: isRetro ? vortxTypography.mono : vortxTypography.body,
                        fontSize: isRetro ? '0.75rem' : '1rem',
                        color: isRetro ? '#888' : 'text.secondary',
                        mb: 4,
                        maxWidth: 400
                    }}>
                        {isRetro
                            ? 'No project records found. Initialize a new project to begin cold storage optimization operations.'
                            : 'Projects help you optimize your cold storage facilities. Create your first one to get started.'}
                    </Typography>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setCreateDialogOpen(true)}
                        sx={isRetro ? {
                            bgcolor: 'transparent',
                            color: '#2196F3',
                            px: 4,
                            py: 1.5,
                            fontSize: '0.85rem',
                            fontWeight: 600,
                            fontFamily: vortxTypography.mono,
                            borderRadius: 0,
                            textTransform: 'uppercase',
                            letterSpacing: '0.1em',
                            border: `1px solid #2196F3`,
                            boxShadow: `0 0 15px rgba(33, 150, 243, 0.2)`,
                            '&:hover': {
                                bgcolor: 'rgba(33, 150, 243, 0.1)',
                                boxShadow: `0 0 25px rgba(33, 150, 243, 0.4)`,
                            }
                        } : {
                            bgcolor: '#2196F3',
                            color: '#FFFFFF',
                            px: 5,
                            py: 2,
                            fontSize: '1.1rem',
                            fontWeight: 600,
                            fontFamily: vortxTypography.body,
                            borderRadius: 2,
                            textTransform: 'none',
                            boxShadow: `0 4px 12px ${alpha('#2196F3', 0.4)}`,
                            '&:hover': {
                                bgcolor: '#1976d2',
                            }
                        }}
                    >
                        {isRetro ? 'Initialize Project' : 'Create Your First Project'}
                    </Button>
                </Box>
            ) : (
                <ProjectTableContainer>
                    <ProjectTableHead>
                        <ProjectTableHeaderCell sx={{ width: 50 }} />
                        <ProjectTableHeaderCell>Project</ProjectTableHeaderCell>
                        <ProjectTableHeaderCell>Facilities</ProjectTableHeaderCell>
                        <ProjectTableHeaderCell>Location</ProjectTableHeaderCell>
                        <ProjectTableHeaderCell align="right">Actions</ProjectTableHeaderCell>
                    </ProjectTableHead>
                    <ProjectTableBody>
                        {projects.map((project) => (
                            <Fragment key={project.id}>
                                {/* Project Row */}
                                <TableRow
                                    className="vx-slide-in-up vx-state-transition"
                                    onClick={() => toggleExpand(project.id)}
                                    sx={isRetro ? {
                                        cursor: 'pointer',
                                        bgcolor: isExpanded(project.id) ? 'rgba(33, 150, 243, 0.1)' : 'rgba(0, 0, 0, 0.4)',
                                        borderLeft: isExpanded(project.id) ? `3px solid #2196F3` : '3px solid transparent',
                                        '&:hover': {
                                            bgcolor: 'rgba(33, 150, 243, 0.15)',
                                        },
                                        '& td': {
                                            borderBottom: '1px solid rgba(63, 106, 100, 0.3)',
                                        }
                                    } : {
                                        cursor: 'pointer',
                                        bgcolor: isExpanded(project.id) ? alpha('#2196F3', 0.08) : 'transparent',
                                        '&:hover': {
                                            bgcolor: alpha('#2196F3', 0.1),
                                        },
                                    }}
                                >
                                    <TableCell sx={{ width: 50 }}>
                                        <IconButton size="small" sx={{ color: isRetro ? '#2196F3' : '#2196F3' }}>
                                            {isExpanded(project.id) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                        </IconButton>
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <ColdIcon sx={{ color: '#2196F3', fontSize: isRetro ? 20 : 24 }} />
                                            <Typography sx={isRetro ? {
                                                fontFamily: vortxTypography.heading,
                                                fontSize: '1rem',
                                                fontWeight: 600,
                                                color: '#f4f1ec',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.05em',
                                            } : {
                                                fontFamily: vortxTypography.heading,
                                                fontSize: '1.1rem',
                                                fontWeight: 600,
                                                color: textPrimary,
                                            }}>
                                                {project.name}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={`${getFacilityCount(project)} facilit${getFacilityCount(project) !== 1 ? 'ies' : 'y'}`}
                                            size="small"
                                            sx={isRetro ? {
                                                bgcolor: 'transparent',
                                                color: '#2196F3',
                                                border: '1px solid rgba(33, 150, 243, 0.3)',
                                                fontFamily: vortxTypography.mono,
                                                fontSize: '0.7rem',
                                                borderRadius: 0,
                                            } : {
                                                bgcolor: alpha('#2196F3', 0.15),
                                                color: isDark ? '#fff' : '#2c2c2c',
                                                fontFamily: vortxTypography.body,
                                                fontWeight: 500,
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <LocationIcon sx={{ fontSize: 16, color: textSecondary }} />
                                            <Typography variant="body2" sx={{ color: textSecondary, fontSize: '0.85rem' }}>
                                                {getProjectLocation(project)}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                                            <IconButton
                                                size="small"
                                                onClick={(e) => openRenameDialog(project, e)}
                                                title="Rename Project"
                                                sx={isRetro ? {
                                                    color: '#666',
                                                    borderRadius: 0,
                                                    '&:hover': { color: '#2196F3', bgcolor: 'rgba(33, 150, 243, 0.1)' }
                                                } : {
                                                    color: vortxColors.gray,
                                                    '&:hover': { color: '#2196F3', bgcolor: alpha('#2196F3', 0.1) }
                                                }}
                                            >
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                onClick={(e) => openDeleteDialog(project, e)}
                                                title="Delete Project"
                                                sx={isRetro ? {
                                                    color: '#666',
                                                    borderRadius: 0,
                                                    '&:hover': { color: '#e74c3c', bgcolor: 'rgba(231, 76, 60, 0.1)' }
                                                } : {
                                                    color: vortxColors.gray,
                                                    '&:hover': { color: '#ff6b6b', bgcolor: alpha('#ff6b6b', 0.1) }
                                                }}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    </TableCell>
                                </TableRow>

                                {/* Expanded Details Section */}
                                <TableRow>
                                    <TableCell colSpan={5} sx={{ p: 0, border: 'none' }}>
                                        <Collapse in={isExpanded(project.id)} timeout="auto" unmountOnExit>
                                            <Box sx={isRetro ? {
                                                bgcolor: 'rgba(0, 0, 0, 0.6)',
                                                borderLeft: `3px solid #2196F3`,
                                                p: 3,
                                                ml: 0,
                                            } : {
                                                bgcolor: alpha('#2196F3', 0.05),
                                                p: 3,
                                                borderLeft: `4px solid #2196F3`,
                                            }}>
                                                <Box sx={{ display: 'flex', gap: 4, mb: 3 }}>
                                                    {/* Project Info */}
                                                    <Box sx={{ flex: 1 }}>
                                                        <Typography sx={{
                                                            fontFamily: isRetro ? vortxTypography.mono : vortxTypography.body,
                                                            fontSize: '0.75rem',
                                                            color: '#2196F3',
                                                            textTransform: 'uppercase',
                                                            fontWeight: 600,
                                                            mb: 1,
                                                        }}>
                                                            Project Details
                                                        </Typography>
                                                        {project.description && (
                                                            <Typography variant="body2" sx={{ color: textSecondary, mb: 2 }}>
                                                                {project.description}
                                                            </Typography>
                                                        )}
                                                        <Typography variant="caption" sx={{ color: textSecondary }}>
                                                            Created {new Date(project.created_at).toLocaleDateString()}
                                                        </Typography>
                                                    </Box>

                                                    {/* Facilities */}
                                                    <Box sx={{ flex: 1 }}>
                                                        <Typography sx={{
                                                            fontFamily: isRetro ? vortxTypography.mono : vortxTypography.body,
                                                            fontSize: '0.75rem',
                                                            color: '#2196F3',
                                                            textTransform: 'uppercase',
                                                            fontWeight: 600,
                                                            mb: 1,
                                                        }}>
                                                            Facilities
                                                        </Typography>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <WarehouseIcon sx={{ fontSize: 16, color: textSecondary }} />
                                                            <Typography variant="body2" sx={{ color: textSecondary }}>
                                                                {getFacilityCount(project)} configured
                                                            </Typography>
                                                        </Box>
                                                    </Box>

                                                    {/* Location */}
                                                    <Box sx={{ flex: 1 }}>
                                                        <Typography sx={{
                                                            fontFamily: isRetro ? vortxTypography.mono : vortxTypography.body,
                                                            fontSize: '0.75rem',
                                                            color: '#2196F3',
                                                            textTransform: 'uppercase',
                                                            fontWeight: 600,
                                                            mb: 1,
                                                        }}>
                                                            Location
                                                        </Typography>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <LocationIcon sx={{ fontSize: 16, color: textSecondary }} />
                                                            <Typography variant="body2" sx={{ color: textSecondary }}>
                                                                {getProjectLocation(project)}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </Box>

                                                {/* Action Button */}
                                                <Button
                                                    variant="contained"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigate(`/cold-storage/${project.id}`);
                                                    }}
                                                    sx={isRetro ? {
                                                        bgcolor: 'transparent',
                                                        color: '#2196F3',
                                                        borderRadius: 0,
                                                        border: '1px solid #2196F3',
                                                        fontFamily: vortxTypography.mono,
                                                        fontSize: '0.75rem',
                                                        textTransform: 'uppercase',
                                                        px: 3,
                                                        '&:hover': {
                                                            bgcolor: 'rgba(33, 150, 243, 0.1)',
                                                        }
                                                    } : {
                                                        bgcolor: '#2196F3',
                                                        color: '#fff',
                                                        '&:hover': {
                                                            bgcolor: '#1976d2',
                                                        }
                                                    }}
                                                >
                                                    Open Project
                                                </Button>
                                            </Box>
                                        </Collapse>
                                    </TableCell>
                                </TableRow>
                            </Fragment>
                        ))}
                    </ProjectTableBody>
                </ProjectTableContainer>
            )}

            {/* Create Project Dialog */}
            <Dialog
                open={createDialogOpen}
                onClose={() => !creating && setCreateDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>New Cold Storage Project</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        fullWidth
                        label="Project Name"
                        value={newProjectName}
                        onChange={(e) => setNewProjectName(e.target.value)}
                        sx={{ mt: 2, mb: 2 }}
                        disabled={creating}
                    />
                    <TextField
                        fullWidth
                        label="Description (optional)"
                        value={newProjectDescription}
                        onChange={(e) => setNewProjectDescription(e.target.value)}
                        multiline
                        rows={3}
                        disabled={creating}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCreateDialogOpen(false)} disabled={creating}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleCreateProject}
                        disabled={!newProjectName.trim() || creating}
                        sx={{
                            background: `linear-gradient(135deg, #2196F3 0%, #42a5f5 100%)`,
                        }}
                    >
                        {creating ? <CircularProgress size={24} /> : 'Create Project'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Rename Dialog */}
            <Dialog
                open={renameDialogOpen}
                onClose={() => setRenameDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Rename Project</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        fullWidth
                        label="Project Name"
                        value={tempProjectName}
                        onChange={(e) => setTempProjectName(e.target.value)}
                        sx={{ mt: 2 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRenameDialogOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleRenameProject}
                        disabled={!tempProjectName.trim()}
                        sx={{ bgcolor: '#2196F3' }}
                    >
                        Rename
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                maxWidth="sm"
            >
                <DialogTitle>Delete Project</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete "{projectToDelete?.name}"? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleDeleteProject}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Add Existing Project Dialog */}
            <AddExistingProjectDialog
                open={addExistingOpen}
                onClose={() => setAddExistingOpen(false)}
                appName="flux_cold"
                appLabel="Flux COLD"
                onProjectAdded={() => loadProjects()}
            />
        </Box>
    );
}
